import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import { postMessageToReactNativeWebView } from "@shared/utils/webview";
import Button from "@ui-kit/form/Button/Button.web";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "next/link";

type DownloadTicketsButtonProps = {
  myOrderUrl: string;
  transactionId: string;
};

/**
 * A button component that allows users to download their tickets.
 * The component adapts its behavior based on the platform:
 * - In web environments: Renders a link to the order page where tickets can be downloaded
 * - In React Native webview: Sends a message to the native app to handle the download
 */
export default function DownloadTicketsButton(
  props: DownloadTicketsButtonProps
): JSX.Element {
  return (
    <WebviewComponentAdaptor
      webComponent={<DownloadTicketsButton.WebComponent {...props} />}
      webviewComponent={<DownloadTicketsButton.WebviewComponent {...props} />}
    />
  );
}

DownloadTicketsButton.WebviewComponent = function WebviewComponent({
  transactionId,
}: Omit<DownloadTicketsButtonProps, "myOrderUrl">) {
  const postOrderMessage = () =>
    postMessageToReactNativeWebView({
      action: "VIEW_ORDER",
      payload: { transactionId },
    });

  return <DownloadTicketsButton.ButtonComponent onClick={postOrderMessage} />;
};

DownloadTicketsButton.WebComponent = function WebComponent({
  myOrderUrl,
}: Omit<DownloadTicketsButtonProps, "transactionId">) {
  return (
    <Link href={myOrderUrl} passHref style={{ width: "100%" }} target="_blank">
      <DownloadTicketsButton.ButtonComponent />
    </Link>
  );
};

DownloadTicketsButton.ButtonComponent = function ButtonComponent({
  onClick,
}: {
  onClick?: () => void;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Button.Solid
      as="span"
      fontWeight={500}
      width="100%"
      data-testid="order-status-download-tickets"
      onClick={onClick}
    >
      {t("Download tickets")}
    </Button.Solid>
  );
};
